import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FILE_CONTENTS } from '../../data/fileSystem';

// Fonction utilitaire debounce pour optimiser les performances
const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(null, args), wait);
    };
};

// Fonction utilitaire pour récupérer le nom d'affichage d'un fichier
const getFileDisplayName = (fileId: string): string => {
    const file = FILE_CONTENTS[fileId];
    if (file) {
        return file.title || fileId;
    }
    return fileId;
};

interface NotificationItem {
    id: string;
    type: 'file' | 'message';
    data: any;
    timestamp: number;
    isViewed?: boolean; // Nouvelle propriété pour tracker si la notification a été vue
}

interface NotificationContainerProps {
    fileNotifications: Array<{
        id: string;
        fileName: string;
        source: string;
    }>;
    messageNotifications: Array<{
        id: string;
        sender: 'Pixel' | 'Reka' | 'System';
        preview: string;
    }>;
    onDismissFile: (id: string) => void;
    onDismissMessage: (id: string) => void;
    onOpenMessenger: () => void;
    isOpen?: boolean; // Contrôle manuel de l'ouverture/fermeture
    onClose?: () => void; // Callback pour fermer le panneau
    onUnviewedCountChange?: (count: number) => void; // Callback pour notifier le changement du nombre de notifications non vues
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
    fileNotifications,
    messageNotifications,
    onDismissFile,
    onDismissMessage,
    onOpenMessenger,
    isOpen = false,
    onClose,
    onUnviewedCountChange
}) => {
    const [displayedNotifications, setDisplayedNotifications] = useState<NotificationItem[]>([]);
    const [isContainerVisible, setIsContainerVisible] = useState(false);
    const [autoCloseTimer, setAutoCloseTimer] = useState<NodeJS.Timeout | null>(null);
    const [notificationTimestamps, setNotificationTimestamps] = useState<Map<string, number>>(new Map());
    const [viewedNotifications, setViewedNotifications] = useState<Set<string>>(() => {
        // Charger les notifications vues depuis localStorage
        const saved = localStorage.getItem('ALBION_VIEWED_NOTIFICATIONS');
        if (saved) {
            try {
                return new Set(JSON.parse(saved));
            } catch (e) {}
        }
        return new Set();
    }); // Notifications vues
    const containerRef = useRef<HTMLDivElement>(null);

    // Sauvegarder les notifications vues (optimisé avec debounce)
    const saveViewedNotifications = useCallback(
        debounce((notifications: Set<string>) => {
            localStorage.setItem('ALBION_VIEWED_NOTIFICATIONS', JSON.stringify(Array.from(notifications)));
        }, 500),
        []
    );

    useEffect(() => {
        saveViewedNotifications(viewedNotifications);
    }, [viewedNotifications, saveViewedNotifications]);

    // Effet optimisé pour les timestamps (seulement quand de nouvelles notifications arrivent)
    useEffect(() => {
        const currentTime = Date.now();
        const allNotificationIds = [...fileNotifications, ...messageNotifications].map(n => n.id);
        
        // Vérifier s'il y a vraiment de nouvelles notifications
        const hasNewNotifications = allNotificationIds.some(id => !notificationTimestamps.has(id));
        
        if (hasNewNotifications) {
            setNotificationTimestamps(prev => {
                const newTimestamps = new Map(prev);
                allNotificationIds.forEach(id => {
                    if (!newTimestamps.has(id)) {
                        newTimestamps.set(id, currentTime);
                    }
                });
                return newTimestamps;
            });
        }
    }, [fileNotifications.length, messageNotifications.length]); // Dépendances optimisées

    // Calculer les notifications affichées (optimisé)
    const allNotifications = React.useMemo(() => {
        const currentTime = Date.now();
        
        const notifications: NotificationItem[] = [
            ...fileNotifications.map(notif => ({
                id: notif.id,
                type: 'file' as const,
                data: notif,
                timestamp: notificationTimestamps.get(notif.id) || currentTime,
                isViewed: viewedNotifications.has(notif.id)
            })),
            ...messageNotifications.map(notif => ({
                id: notif.id,
                type: 'message' as const,
                data: notif,
                timestamp: notificationTimestamps.get(notif.id) || currentTime,
                isViewed: viewedNotifications.has(notif.id)
            }))
        ];

        // Trier par timestamp (plus récent en premier)
        notifications.sort((a, b) => b.timestamp - a.timestamp);
        
        return notifications;
    }, [fileNotifications, messageNotifications, notificationTimestamps, viewedNotifications]);

    // Mettre à jour les notifications affichées (seulement si changement)
    useEffect(() => {
        setDisplayedNotifications(allNotifications);
    }, [allNotifications]);

    // Calculer et notifier le nombre de notifications non vues (optimisé)
    const unviewedCount = React.useMemo(() => {
        return allNotifications.filter(notif => !notif.isViewed).length;
    }, [allNotifications]);

    useEffect(() => {
        onUnviewedCountChange?.(unviewedCount);
    }, [unviewedCount, onUnviewedCountChange]);

    const handleCloseWithAnimation = useCallback(() => {
        setIsContainerVisible(false);
        setTimeout(() => {
            onClose?.();
        }, 500); // Durée de l'animation de glissement
    }, [onClose]);

    // Note: L'effet onCloseWithAnimation a été supprimé car il causait une boucle infinie

    // Effet pour gérer la visibilité du conteneur (optimisé)
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const hasUnviewedNotifications = React.useMemo(() => {
        return displayedNotifications.some(n => !n.isViewed);
    }, [displayedNotifications]);

    useEffect(() => {
        // Si ouvert manuellement, toujours visible
        if (isOpen) {
            setIsContainerVisible(true);
            // Annuler le timer d'auto-fermeture
            if (autoCloseTimer) {
                clearTimeout(autoCloseTimer);
                setAutoCloseTimer(null);
            }
        }
        // Si pas ouvert manuellement mais qu'il y a des notifications non vues
        else if (hasUnviewedNotifications) {
            setIsContainerVisible(true);
            
            // Réinitialiser le timer d'auto-fermeture
            if (autoCloseTimer) {
                clearTimeout(autoCloseTimer);
            }
            
            // Programmer la fermeture automatique après 3 secondes
            const newTimer = setTimeout(() => {
                // Fermer seulement si pas ouvert manuellement
                if (!isOpenRef.current) {
                    setIsContainerVisible(false);
                }
            }, 3000);
            
            setAutoCloseTimer(newTimer);
        }
        // Si pas de notifications non vues et pas ouvert manuellement
        else if (!isOpen) {
            setIsContainerVisible(false);
        }
    }, [isOpen, hasUnviewedNotifications]); // Dépendances optimisées

    // Marquer les notifications comme vues (effet séparé et optimisé)
    useEffect(() => {
        if (isOpen && displayedNotifications.length > 0) {
            const allIds = displayedNotifications.map(n => n.id);
            setViewedNotifications(prev => {
                const newSet = new Set(prev);
                let hasChanges = false;
                allIds.forEach(id => {
                    if (!newSet.has(id)) {
                        newSet.add(id);
                        hasChanges = true;
                    }
                });
                return hasChanges ? newSet : prev;
            });
        }
    }, [isOpen, displayedNotifications.length]); // Optimisé avec .length

    // Effet pour détecter les clics à l'extérieur et fermer le panneau (optimisé)
    useEffect(() => {
        if (!isOpen) return; // Ne pas ajouter l'event listener si pas ouvert

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                handleCloseWithAnimation();
            }
        };

        // Délai pour éviter la fermeture immédiate lors de l'ouverture
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, handleCloseWithAnimation]);

    const handleDismissNotification = useCallback((id: string, type: 'file' | 'message') => {
        // Supprimer la notification de la liste affichée
        setDisplayedNotifications(prev => prev.filter(notif => notif.id !== id));
        
        // Supprimer aussi des notifications vues
        setViewedNotifications(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        
        // Appeler le callback approprié pour supprimer des hooks
        if (type === 'file') {
            onDismissFile(id);
        } else {
            onDismissMessage(id);
        }
    }, [onDismissFile, onDismissMessage]);

    if (!isContainerVisible && !isOpen && displayedNotifications.length === 0) {
        return null;
    }

    return (
        <div 
            ref={containerRef}
            className={`
                fixed top-12 right-4 z-[9999] space-y-2 w-96
                transition-all duration-500 ease-out
                ${(isContainerVisible || isOpen) ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
            `}
        >
            {/* Conteneur avec fond semi-transparent */}
            <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-600 rounded-lg shadow-2xl shadow-black/40 p-2">
                {/* Header du conteneur */}
                <div className="text-xs text-slate-400 font-mono uppercase tracking-wider mb-2 px-2 py-1 border-b border-slate-700">
                    Notifications ({displayedNotifications.length})
                </div>
                
                {/* Liste des notifications */}
                <div className="space-y-2 max-h-96 overflow-y-auto albion-scrollbar">
                    {displayedNotifications.length > 0 ? (
                        displayedNotifications.map((notif, index) => (
                            <div 
                                key={notif.id}
                                className={`
                                    transform transition-all duration-300 ease-out
                                    ${index === 0 ? 'scale-100' : 'scale-95 opacity-80'}
                                `}
                                style={{
                                    animationDelay: `${index * 100}ms`
                                }}
                            >
                                {notif.type === 'file' ? (
                                    <NotificationItem
                                        type="file"
                                        data={notif.data}
                                        onDismiss={() => handleDismissNotification(notif.id, 'file')}
                                        isViewed={notif.isViewed}
                                    />
                                ) : (
                                    <NotificationItem
                                        type="message"
                                        data={notif.data}
                                        onDismiss={() => handleDismissNotification(notif.id, 'message')}
                                        onOpenMessenger={onOpenMessenger}
                                        isViewed={notif.isViewed}
                                    />
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-8 text-slate-400 text-sm font-mono">
                            <div className="text-slate-500 mb-2">◆</div>
                            <div>Aucune notification</div>
                        </div>
                    )}
                </div>
                
                {/* Barre de progression pour l'auto-fermeture */}
                {!isOpen && displayedNotifications.some(n => !n.isViewed) && autoCloseTimer && (
                    <div className="mt-2 h-1 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full"
                            style={{
                                width: '100%',
                                animation: 'countdown 3s linear forwards'
                            }}
                        />
                    </div>
                )}
            </div>
            
            <style>{`
                @keyframes countdown {
                    from {
                        width: 100%;
                    }
                    to {
                        width: 0%;
                    }
                }
            `}</style>
        </div>
    );
};

// Composant pour afficher une notification individuelle dans la liste
const NotificationItem: React.FC<{
    type: 'file' | 'message';
    data: any;
    onDismiss: () => void;
    onOpenMessenger?: () => void;
    isViewed?: boolean;
}> = ({ type, data, onDismiss, onOpenMessenger, isViewed = false }) => {
    if (type === 'file') {
        return (
            <div 
                className={`border rounded p-3 text-sm font-mono cursor-pointer transition-colors ${
                    isViewed 
                        ? 'opacity-70 border-cyan-600/30 bg-cyan-950/25 hover:bg-cyan-950/35' 
                        : 'border-cyan-500/50 bg-cyan-950/30 hover:bg-cyan-900/40'
                }`}
                onClick={() => {
                    // Cas spécial pour les notifications de fond d'écran
                    if (data.fileName === 'Fond d\'écran de certification' || data.fileName.includes('fond d\'écran') || data.fileName.includes('wallpaper')) {
                        // Ouvrir le sélecteur de fond d'écran
                        window.dispatchEvent(new CustomEvent('OPEN_WALLPAPER_SELECTOR'));
                        console.log('📱 Ouverture du sélecteur de fond d\'écran via notification');
                    } else {
                        // Comportement normal : ouvrir l'explorateur et localiser le fichier
                        window.dispatchEvent(new CustomEvent('OPEN_EXPLORER_AND_HIGHLIGHT', {
                            detail: { fileId: data.fileName }
                        }));
                    }
                    onDismiss();
                }}
            >
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isViewed ? 'bg-cyan-600/50' : 'bg-cyan-400 animate-pulse'}`}></div>
                        <span className={`font-bold text-xs ${isViewed ? 'text-cyan-500/70' : 'text-cyan-400'}`}>FICHIER</span>
                        {isViewed && <span className="text-cyan-600/50 text-[8px]">VUE</span>}
                    </div>
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            onDismiss();
                        }}
                        className="text-slate-400 hover:text-cyan-400 transition-colors"
                    >
                        ×
                    </button>
                </div>
                <div className={`font-bold truncate ${isViewed ? 'text-cyan-300/60' : 'text-slate-200'}`}>
                    {getFileDisplayName(data.fileName)}
                </div>
                <div className={`text-xs mt-1 ${isViewed ? 'text-cyan-400/50' : 'text-slate-400'}`}>
                    Source: {data.source}
                    {(data.fileName === 'Fond d\'écran de certification' || data.fileName.includes('fond d\'écran') || data.fileName.includes('wallpaper')) ? (
                        <span className="ml-2 text-cyan-400/70">→ Cliquer pour changer le fond d'écran</span>
                    ) : (
                        <span className="ml-2 text-cyan-400/70">→ Cliquer pour localiser</span>
                    )}
                </div>
            </div>
        );
    } else {
        return (
            <div 
                className={`border rounded p-3 text-sm font-mono cursor-pointer transition-colors ${
                    isViewed 
                        ? 'opacity-70 border-green-600/30 bg-green-950/25 hover:bg-green-950/35' 
                        : 'border-green-500/50 bg-green-950/30 hover:bg-green-900/40'
                }`}
                onClick={() => {
                    onOpenMessenger?.();
                    onDismiss();
                }}
            >
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${isViewed ? 'bg-green-600/50' : 'bg-green-400 animate-pulse'}`}></div>
                        <span className={`font-bold text-xs ${isViewed ? 'text-green-500/70' : 'text-green-400'}`}>MESSAGE</span>
                        {isViewed && <span className="text-green-600/50 text-[8px]">VUE</span>}
                    </div>
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            onDismiss();
                        }}
                        className="text-slate-400 hover:text-cyan-400 transition-colors"
                    >
                        ×
                    </button>
                </div>
                <div className={`font-bold ${isViewed ? 'text-green-300/60' : 'text-slate-200'}`}>{data.sender}</div>
                <div className={`text-xs mt-1 line-clamp-2 ${isViewed ? 'text-green-400/50' : 'text-slate-300'}`}>{data.preview}</div>
            </div>
        );
    }
};